import { useEffect, useMemo, useState } from 'react';

import type { CurateBlock as Block, CurateResponse } from '@/api/curate';
import { num, readQuestion, str, strList } from './blocks';
import { sanitizeHtml } from './sanitizeHtml';
import styles from './CurateBlock.module.css';

interface CurateBlockProps {
  block: Block;
  /** Saved answer for this block, when it is a question and has been answered. */
  response?: CurateResponse;
  /** Persist an answer. Omitted when the article is read-only (completed). */
  onAnswer?: (blockId: string, answer: { selectedOptions?: number[]; freeText?: string }) => void;
  /** True while this block's answer is in flight. */
  saving?: boolean;
  /** Read-only: the assignment is complete, so answers are shown but locked. */
  locked?: boolean;
}

/**
 * Render one Kai-Studio content block.
 *
 * Covers the ten block types in the Curate contract. An unrecognized type
 * renders nothing rather than throwing, so a future Kai-Studio block shipped
 * ahead of this app degrades to a gap instead of a blank reader.
 */
export function CurateBlockView({ block, response, onAnswer, saving, locked }: CurateBlockProps) {
  const p = block.payload ?? {};

  switch (block.blockType) {
    case 'heading': {
      const text = str(p, 'text');
      if (!text) return null;
      // Clamp to h3-h5: the modal already owns h1/h2 for the article title, so
      // honoring an authored level of 1 would break the document outline.
      const level = Math.min(Math.max(num(p, 'level', 2), 1), 3);
      const Tag = (['h3', 'h4', 'h5'] as const)[level - 1];
      return <Tag className={styles[`heading${level}` as keyof typeof styles]}>{text}</Tag>;
    }

    case 'rich_text': {
      const html = sanitizeHtml(str(p, 'html'));
      if (!html) return null;
      return <div className={styles.richText} dangerouslySetInnerHTML={{ __html: html }} />;
    }

    case 'bullet_list': {
      const items = strList(p, 'items');
      if (items.length === 0) return null;
      return (
        <ul className={styles.bulletList}>
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    }

    case 'numbered_list': {
      const items = strList(p, 'items');
      if (items.length === 0) return null;
      return (
        <ol className={styles.numberedList}>
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );
    }

    case 'image': {
      const url = str(p, 'url');
      const caption = str(p, 'caption');
      if (!url) return null;
      return (
        <figure className={styles.figure}>
          {/* alt falls back to the caption; empty alt is correct for a purely
              decorative image and avoids screen readers reading a filename. */}
          <img className={styles.image} src={url} alt={caption} loading="lazy" />
          {caption && <figcaption className={styles.caption}>{caption}</figcaption>}
        </figure>
      );
    }

    case 'quote': {
      const text = str(p, 'text');
      const attribution = str(p, 'attribution');
      if (!text) return null;
      return (
        <blockquote className={styles.quote}>
          <p className={styles.quoteText}>{text}</p>
          {attribution && <cite className={styles.quoteCite}>{attribution}</cite>}
        </blockquote>
      );
    }

    case 'callout': {
      const text = str(p, 'text');
      if (!text) return null;
      const rawTone = str(p, 'tone');
      const tone = (['info', 'success', 'warning'] as const).includes(
        rawTone as 'info' | 'success' | 'warning',
      )
        ? rawTone
        : 'info';
      return (
        <aside className={`${styles.callout} ${styles[`callout_${tone}`]}`}>
          <span className={styles.calloutText}>{text}</span>
        </aside>
      );
    }

    case 'divider':
      return <hr className={styles.divider} />;

    case 'question':
      return (
        <QuestionBlock
          block={block}
          response={response}
          onAnswer={onAnswer}
          saving={saving}
          locked={locked}
        />
      );

    // page_break is a separator consumed by groupBlocksByPage, never rendered.
    case 'page_break':
      return null;

    default:
      return null;
  }
}

/* Question block
 * -------------------------------------------------------------------------- */

type QuestionBlockProps = CurateBlockProps;

/**
 * An answerable question.
 *
 * Selection is local state so the UI responds instantly, but correctness comes
 * only from the saved `response` returned by the server. The payload's
 * `correct` flags are deliberately never read here — grading lives server-side
 * against the pinned block payload, and duplicating it client-side would let
 * the two drift and would expose the answer key.
 *
 * Multi-select defers the write until Submit, because each intermediate
 * selection would otherwise be graded and increment `attemptCount`. Single
 * choice and free text write on commit too, keeping one save per deliberate
 * answer rather than one per keystroke or click.
 */
function QuestionBlock({ block, response, onAnswer, saving, locked }: QuestionBlockProps) {
  const question = useMemo(() => readQuestion(block.payload ?? {}), [block.payload]);
  const { prompt, responseType, options } = question;

  const [selected, setSelected] = useState<number[]>(response?.selectedOptions ?? []);
  const [text, setText] = useState<string>(response?.freeText ?? '');

  // Re-sync when the saved answer arrives or changes (e.g. the reader query
  // refetched after a save, or the learner reopened the article).
  useEffect(() => {
    setSelected(response?.selectedOptions ?? []);
    setText(response?.freeText ?? '');
  }, [response?.selectedOptions, response?.freeText]);

  const readOnly = locked || !onAnswer;
  const answered = !!response;
  const graded = answered && response?.isCorrect !== null && response?.isCorrect !== undefined;

  const commitChoice = (indexes: number[]) => {
    setSelected(indexes);
    if (!readOnly) onAnswer?.(block.blockId, { selectedOptions: indexes });
  };

  const toggleMulti = (index: number) => {
    setSelected((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index].sort((a, b) => a - b),
    );
  };

  const name = `q-${block.blockId}`;

  return (
    <section
      className={`${styles.question} ${graded ? (response?.isCorrect ? styles.questionCorrect : styles.questionIncorrect) : ''}`}
      aria-labelledby={`${name}-prompt`}
    >
      <div className={styles.questionHead}>
        <span className={styles.questionBadge}>Question</span>
        {answered && (
          <span className={styles.questionState}>
            {graded ? (response?.isCorrect ? 'Correct' : 'Not quite') : 'Answered'}
            {response && response.attemptCount > 1 && (
              <span className={styles.attempts}> · attempt {response.attemptCount}</span>
            )}
          </span>
        )}
      </div>

      <p id={`${name}-prompt`} className={styles.questionPrompt}>
        {prompt}
      </p>

      {responseType === 'free_text' ? (
        <div className={styles.freeText}>
          <label className={styles.srOnly} htmlFor={`${name}-input`}>
            Your answer
          </label>
          <textarea
            id={`${name}-input`}
            className={styles.textarea}
            value={text}
            rows={4}
            disabled={readOnly || saving}
            placeholder="Type your answer"
            onChange={(e) => setText(e.target.value)}
          />
          {!readOnly && (
            <button
              type="button"
              className={styles.submit}
              disabled={saving || text.trim().length === 0 || text === (response?.freeText ?? '')}
              onClick={() => onAnswer?.(block.blockId, { freeText: text })}
            >
              {saving ? 'Saving…' : answered ? 'Update answer' : 'Save answer'}
            </button>
          )}
        </div>
      ) : (
        <>
          <ul className={styles.options} role={responseType === 'single_choice' ? 'radiogroup' : 'group'}>
            {options.map((option, index) => {
              const isSelected = selected.includes(index);
              return (
                <li key={index}>
                  <label
                    className={`${styles.option} ${isSelected ? styles.optionSelected : ''} ${readOnly ? styles.optionLocked : ''}`}
                  >
                    <input
                      type={responseType === 'single_choice' ? 'radio' : 'checkbox'}
                      name={name}
                      className={styles.optionInput}
                      checked={isSelected}
                      disabled={readOnly || saving}
                      onChange={() =>
                        responseType === 'single_choice' ? commitChoice([index]) : toggleMulti(index)
                      }
                    />
                    <span className={styles.optionLabel}>{option.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>

          {/* Multi-select commits explicitly so partial selections are not
              graded on the way to a complete answer. */}
          {responseType === 'multi_select' && !readOnly && (
            <button
              type="button"
              className={styles.submit}
              disabled={saving || selected.length === 0 || sameSet(selected, response?.selectedOptions)}
              onClick={() => onAnswer?.(block.blockId, { selectedOptions: selected })}
            >
              {saving ? 'Saving…' : answered ? 'Update answer' : 'Submit answer'}
            </button>
          )}
        </>
      )}

      {/* Feedback comes from the SAVED response only, so it can never appear
          before the server has graded the attempt. */}
      {graded && !response?.isCorrect && (
        <p className={styles.feedback}>Review this section and try again.</p>
      )}
    </section>
  );
}

const sameSet = (a: number[], b: number[] | null | undefined): boolean => {
  if (!b) return false;
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort((x, y) => x - y);
  return a.every((v, i) => v === sortedB[i]);
};
